import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  MessageCircle,
  Smartphone,
  Mail,
  Phone,
  Eye,
  EyeOff,
  Check,
  Loader2,
  AlertCircle,
  Sparkles,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * OrderChannelsForm — reusable settings card for the four customer-facing
 * order-receiving channels. Used both inside the Dashboard Settings tab
 * and the public Onboarding "Setup" page. Drives the same backend route
 * (`PATCH /api/restaurants/{id}/order-channels`).
 *
 * Props:
 *   restaurantId — owner's restaurant uuid
 *   token        — restaurant-owner JWT (Authorization: Bearer)
 *   onChange?    — optional `(channels, ready) => void` callback. Lets the
 *                  parent re-check publish-readiness in real time.
 *   compact?     — when true, removes the outer card chrome so the form
 *                  can be embedded inside the onboarding shell.
 */
const CHANNEL_DEFS = [
  { key: 'whatsapp', Icon: MessageCircle, color: 'emerald', field: 'number',
    placeholder: '+1 555 123 4567' },
  { key: 'sms',      Icon: Smartphone,    color: 'blue',    field: 'number',
    placeholder: '+1 555 123 4567' },
  { key: 'email',    Icon: Mail,          color: 'amber',   field: 'email',
    placeholder: 'orders@yourrestaurant.com' },
  { key: 'phone',    Icon: Phone,         color: 'rose',    field: 'number',
    placeholder: '+1 555 123 4567' },
];

// Tailwind needs literal classnames — no dynamic class strings.
const TONE_CLASSES = {
  emerald: 'border-emerald-500/40 bg-emerald-500/[0.06] text-emerald-300',
  blue:    'border-blue-500/40    bg-blue-500/[0.06]    text-blue-300',
  amber:   'border-amber-500/40   bg-amber-500/[0.06]   text-amber-300',
  rose:    'border-rose-500/40    bg-rose-500/[0.06]    text-rose-300',
};

// Local validators — mirror the server regexes so we can suppress
// 400-error saves on partial input (e.g. "a", "a@", "a@b") and stop
// the banner from popping in/out mid-keystroke (the cause of the
// "screen jumping every few digits" UX bug).
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_RE = /^[+\d][\d\s().-]{6,30}$/;
const isValueValid = (field, trimmed) => {
  if (!trimmed) return true;             // empty = clearing → save
  if (field === 'email') return EMAIL_RE.test(trimmed);
  return PHONE_RE.test(trimmed);
};

const OrderChannelsForm = ({ restaurantId, token, onChange, compact = false }) => {
  const { t } = useTranslation();
  const [channels, setChannels] = useState(null);
  const [ready, setReady] = useState(false);
  const [missing, setMissing] = useState([]);
  const [savingKey, setSavingKey] = useState(null);
  // Channel key that just had a successful save — drives the ephemeral
  // "Saved ✓" pill. Cleared after ~1.5s so it doesn't linger and become
  // visual noise. Stored as { key, at } so the pill timestamp is stable
  // even if multiple saves run in quick succession.
  const [savedAt, setSavedAt] = useState({});
  const [error, setError] = useState('');
  // Debounce timers, one per channel, so auto-save fires 500ms after the
  // user stops typing — instead of on every keystroke. Tracked in a ref so
  // state updates never recreate them.
  const debounceTimers = useRef({});
  // Per-channel timers that hide the "Saved ✓" pill after 1.5s.
  const savedTimers = useRef({});

  const auth = { headers: { Authorization: `Bearer ${token}` } };

  // Initial load.
  useEffect(() => {
    if (!restaurantId || !token) return;
    let cancelled = false;
    axios.get(`${API}/restaurants/${restaurantId}/order-channels`, auth)
      .then((r) => {
        if (cancelled) return;
        setChannels(r.data?.channels || null);
        setReady(!!r.data?.ready);
        setMissing(r.data?.missing || []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      // Flush any pending debounce timers when the form unmounts so we
      // never call setState on an unmounted component.
      Object.values(debounceTimers.current).forEach((id) => clearTimeout(id));
      Object.values(savedTimers.current).forEach((id) => clearTimeout(id));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId, token]);

  // ── Persist a single-channel patch ────────────────────────────────────
  // After the server responds, we MERGE only the patched channel back into
  // local state — preserving any edits the user has made to other channels
  // in the meantime (the previous full-replace caused the "section
  // collapses / value disappears" UX bug).
  // Tracks the readiness signal we last reported to the parent so we only
  // fire `onChange` when something the parent CARES about changed
  // (channel enabled flipped or overall ready flipped). Without this, the
  // parent OnboardingSetupPage was refetching readiness on every
  // keystroke-debounced save → full re-render → input jump.
  const lastReported = useRef({ ready: null, enabledMap: '' });

  const patchChannel = async (key, partial) => {
    setSavingKey(key);
    setError('');
    try {
      const r = await axios.patch(
        `${API}/restaurants/${restaurantId}/order-channels`,
        { [key]: partial },
        auth,
      );
      const server = r.data?.channels?.[key];
      if (server) {
        setChannels((prev) => ({ ...(prev || {}), [key]: server }));
      }
      setReady(!!r.data?.ready);
      setMissing(r.data?.missing || []);

      // Only notify parent on MEANINGFUL changes — toggle flips, ready
      // flips, or visibility flips. Pure value typing (number/email)
      // never bubbles up, so the parent never re-renders mid-keystroke.
      const newReady = !!r.data?.ready;
      const enabledMap = JSON.stringify(
        Object.fromEntries(
          Object.entries(r.data?.channels || {}).map(([k, v]) => [
            k, { en: !!v?.enabled, vis: v?.visible_to_customers !== false },
          ]),
        ),
      );
      if (
        lastReported.current.ready !== newReady ||
        lastReported.current.enabledMap !== enabledMap
      ) {
        lastReported.current = { ready: newReady, enabledMap };
        onChange?.(r.data?.channels, newReady);
      }

      // Surface the ephemeral "Saved ✓" pill — clears after 1.5s.
      const stamp = Date.now();
      setSavedAt((prev) => ({ ...prev, [key]: stamp }));
      if (savedTimers.current[key]) clearTimeout(savedTimers.current[key]);
      savedTimers.current[key] = setTimeout(() => {
        setSavedAt((prev) => {
          // Only clear if this is still the most recent save for the key
          // (otherwise a later save's timer would erase a fresh pill).
          if (prev[key] !== stamp) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      }, 1500);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
        t('order_channels.save_failed', 'Could not save. Please try again.'),
      );
    } finally {
      setSavingKey(null);
    }
  };

  // Note: debounced auto-save logic now lives inline inside the input's
  // onChange handler — it needs to send BOTH the value AND the derived
  // `enabled` flag together, so the standalone helper was inlined.

  if (!channels) {
    return (
      <div className="p-6 text-center text-white/55 text-sm" data-testid="order-channels-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
        {t('order_channels.loading', 'Loading channels…')}
      </div>
    );
  }

  // Outer wrapper className depends on `compact` — kept inline (no inner
  // component definition!) because defining a component INSIDE another
  // component changes its type identity on every render, which made React
  // unmount + remount every child on every state update. That was the
  // root cause of the "focus lost on first keystroke" bug.
  const wrapperClass = compact
    ? ''
    : 'rounded-2xl border border-white/10 bg-white/[0.02] p-5 sm:p-6';

  return (
    <div data-testid="order-channels-form" className={wrapperClass}>
      {!compact && (
        <div className="mb-5">
          <h3 className="text-[18px] font-bold text-white tracking-tight">
            {t('order_channels.title', 'Order Receiving Channels')}
          </h3>
          <p className="mt-1.5 text-[13px] text-white/55 leading-relaxed">
            {t('order_channels.subtitle', 'Choose how you want to receive customer orders and notifications. Customers will only see the channels you enable.')}
          </p>
        </div>
      )}

      {/* Recommendation banner */}
      <div
        data-testid="order-channels-recommendation"
        className="mb-4 flex items-start gap-2.5 p-3 rounded-xl border border-yellow-500/30 bg-yellow-500/[0.06]"
      >
        <Sparkles className="w-4 h-4 text-yellow-300 mt-0.5 shrink-0" />
        <p className="text-[12.5px] text-yellow-100/85 leading-relaxed">
          {t('order_channels.recommendation', 'Recommended: Enable as many communication channels as possible. Restaurants that offer multiple ordering and communication options typically receive more customer engagement and higher order conversion rates.')}
        </p>
      </div>

      {/* Ready / not ready banner */}
      {ready ? (
        <div
          data-testid="order-channels-ready"
          className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/[0.08] text-emerald-300 text-[12px] font-bold"
        >
          <Check className="w-3.5 h-3.5" />
          {t('order_channels.ready', 'Ready to publish — at least one channel enabled')}
        </div>
      ) : (
        <div
          data-testid="order-channels-not-ready"
          className="mb-4 flex items-start gap-2 p-3 rounded-xl border border-rose-500/40 bg-rose-500/[0.06]"
        >
          <AlertCircle className="w-4 h-4 text-rose-300 mt-0.5 shrink-0" />
          <p className="text-[12.5px] text-rose-200">
            {t('order_channels.not_ready', 'Please enable at least one order receiving channel before publishing your restaurant.')}
          </p>
        </div>
      )}

      {error && (
        <div
          data-testid="order-channels-error"
          className="mb-3 px-3 py-2 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-200 text-[12px]"
        >
          {String(error)}
        </div>
      )}

      <ul className="space-y-3">
        {CHANNEL_DEFS.map(({ key, Icon, color, field, placeholder }) => {
          const ch = channels[key] || {};
          const enabled = !!ch.enabled;
          const visible = ch.visible_to_customers !== false;
          const value = ch[field] || '';
          const saving = savingKey === key;
          return (
            <li
              key={key}
              data-testid={`channel-row-${key}`}
              className={`p-3.5 rounded-xl border transition-colors ${
                enabled ? TONE_CLASSES[color] : 'border-white/10 bg-white/[0.015]'
              }`}
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2.5">
                  <span className={`w-8 h-8 rounded-lg border flex items-center justify-center shrink-0 ${
                    enabled ? TONE_CLASSES[color] : 'border-white/10 bg-white/[0.03] text-white/45'
                  }`}>
                    <Icon className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="text-[13.5px] font-bold text-white leading-tight">
                      {t(`order_channels.channel_${key}_label`)}
                    </p>
                    <p className="text-[11.5px] text-white/50 leading-tight">
                      {t(`order_channels.channel_${key}_desc`)}
                    </p>
                  </div>
                </div>

                {/* Enable / disable toggle */}
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  data-testid={`channel-toggle-${key}`}
                  onClick={() => patchChannel(key, { enabled: !enabled })}
                  disabled={saving}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    enabled ? 'bg-emerald-500' : 'bg-white/15'
                  } disabled:opacity-50`}
                >
                  <span
                    className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                      enabled ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>

              {/* Contact info + visibility — input is ALWAYS rendered so
                  every channel behaves identically (no surprise "I have
                  to flip a toggle first" moment). Focusing the field OR
                  typing a value auto-enables the channel; clearing the
                  field auto-disables it. The explicit toggle remains as
                  an override for power users. */}
              <div className="mt-2.5 space-y-2 pl-10.5">
                <input
                  type={field === 'email' ? 'email' : 'tel'}
                  value={value}
                  placeholder={placeholder}
                  onFocus={() => {
                    // Click-to-edit ergonomics: tapping the field IS the
                    // intent to enable. No-op if already on so we don't
                    // churn the backend for nothing.
                    if (!enabled) patchChannel(key, { enabled: true });
                  }}
                  onChange={(e) => {
                    const v = e.target.value;
                    const trimmed = v.trim();
                    // Clear any stale error from a previous partial-input
                    // save attempt — the user is actively fixing it.
                    if (error) setError('');
                    // Local update first — keeps the input controlled and
                    // never drops a keystroke.
                    setChannels((prev) => ({
                      ...(prev || {}),
                      [key]: {
                        ...(prev?.[key] || {}),
                        [field]: v,
                        // Auto-enable as soon as the user types something
                        // real; auto-disable when they wipe the field.
                        // Server gets the canonical state via scheduleSave.
                        enabled: trimmed.length > 0,
                      },
                    }));
                    // Debounced save — but ONLY when the value is locally
                    // valid (or empty). Partial input like "a@" used to
                    // get PATCHed → 400 → error banner → layout shift →
                    // PATCHed again → success → banner removed → another
                    // layout shift. The user perceived this as "screen
                    // flickering every few digits". With local validation
                    // we keep typing silent and only save once the value
                    // is dial-able / email-able.
                    if (debounceTimers.current[key]) clearTimeout(debounceTimers.current[key]);
                    if (isValueValid(field, trimmed)) {
                      debounceTimers.current[key] = setTimeout(() => {
                        patchChannel(key, {
                          [field]: trimmed,
                          enabled: trimmed.length > 0,
                        });
                      }, 500);
                    }
                    // Else: silently keep local state until the value
                    // becomes complete. No save, no error, no shift.
                  }}
                  data-testid={`channel-input-${key}`}
                  className={`w-full bg-zinc-900 border rounded-lg px-3 py-2 text-[13px] text-white placeholder-white/35 outline-none transition-colors ${
                    enabled
                      ? 'border-zinc-800 focus:border-yellow-500'
                      : 'border-zinc-900 hover:border-zinc-700 focus:border-yellow-500'
                  }`}
                />

                {/* Visibility toggle + helper text. Only meaningful when
                    the channel is actually enabled — otherwise we render
                    nothing here to keep the row compact. */}
                {enabled && (
                  <>
                    <button
                      type="button"
                      onClick={() => patchChannel(key, { visible_to_customers: !visible })}
                      disabled={saving}
                      data-testid={`channel-visibility-${key}`}
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-bold transition-colors ${
                        visible
                          ? 'border-emerald-500/40 text-emerald-300 bg-emerald-500/[0.05]'
                          : 'border-white/15 text-white/55 bg-white/[0.02]'
                      } disabled:opacity-50`}
                    >
                      {visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                      {visible
                        ? t('order_channels.visible_to_customers', 'Visible to customers')
                        : t('order_channels.hidden_from_customers', 'Internal only')}
                    </button>
                    <p
                      data-testid={`channel-visibility-hint-${key}`}
                      className="text-[11px] text-white/45 leading-snug"
                    >
                      {t(
                        'order_channels.visibility_hint',
                        'When enabled, customers will see this contact method during checkout and ordering.',
                      )}
                    </p>

                    {/* Fixed-height status row — reserves space for the
                        Saving / Saved / nothing states so the layout
                        NEVER shifts mid-keystroke. The earlier flicker
                        was caused by these pills changing the row
                        height as they appeared and disappeared. */}
                    <div className="h-5 flex items-center" aria-live="polite">
                      {saving ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-white/55">
                          <Loader2 className="w-3 h-3 animate-spin" />
                          {t('order_channels.saving', 'Saving…')}
                        </span>
                      ) : savedAt[key] ? (
                        /* Ephemeral "Saved ✓" pill — appears for 1.5s
                           after a successful debounced save. */
                        <span
                          data-testid={`channel-saved-${key}`}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/[0.10] text-[10.5px] font-bold text-emerald-300"
                          style={{ animation: 'nofee-pill-in 200ms ease-out both' }}
                        >
                          <Check className="w-3 h-3" strokeWidth={2.4} />
                          {t('order_channels.saved', 'Saved')}
                        </span>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {/* Future channels footnote */}
      <p
        data-testid="order-channels-future"
        className="mt-4 text-[11px] text-white/40 italic leading-relaxed"
      >
        {t('order_channels.future_note', 'Coming soon: Push notifications, POS integrations, and third-party order integrations.')}
      </p>
    </div>
  );
};

export default OrderChannelsForm;
